/**
 * Historial y reportes de ganancias (Req. 16).
 *
 * Lee ventas y destinos ya registrados y los suma. NO recalcula costos: los
 * costos vienen congelados desde la produccion y prorrateados al registrar la
 * venta. Recalcularlos aqui haria que el historial cambiara cada vez que suben
 * los precios, que es exactamente lo que el congelado existe para evitar.
 */

import { db } from '../schema';
import { hoy, etiquetaMes, formatearMedio, type FechaLocal } from '@/domain/dates';
import {
  resumirPeriodo,
  semanasDelMes,
  rangoDe,
  type Granularidad,
  type ResumenPeriodo,
  type VentaParaResumen,
  type SobranteParaResumen,
} from '@/domain/profit';
import { DESTINOS_SOBRANTE, type DestinoSobrante } from '../types';
import type { Centavos } from '@/domain/money';
import type { PlannedItem, Recipe, Sale } from '../types';
import { ventasEntre, destinosEntre } from './sales';

// ---------------------------------------------------------------------------
// Resumen de un periodo
// ---------------------------------------------------------------------------

export interface ResumenConDestinos extends ResumenPeriodo {
  /** Destinos con su nombre legible en lugar de la clave. */
  sobrantesLegibles: Array<{
    tipo: DestinoSobrante;
    etiqueta: string;
    porciones: number;
    costoCentavos: Centavos;
  }>;
}

async function datosDelRango(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<{ ventas: VentaParaResumen[]; sobrantes: SobranteParaResumen[] }> {
  const ventas = await ventasEntre(desde, hasta);
  const destinos = await destinosEntre(desde, hasta);

  // Una venta se marca como estimada si su produccion lo estaba.
  const producciones = new Map(
    (await db.productions.toArray()).map((p) => [p.id, p]),
  );

  return {
    ventas: ventas.map((venta) => ({
      fecha: venta.fecha,
      porcionesVendidas: venta.porcionesVendidas,
      ingresoCentavos: venta.ingresoCentavos,
      costoCentavos: venta.costoCentavos,
      estimado:
        venta.productionId === null
          ? false
          : (producciones.get(venta.productionId)?.costoEstimado ?? false),
      // Se arrastra desde la produccion: si a la receta le faltaba el precio de
      // un insumo, el costo de la venta es parcial aunque traiga una cifra.
      incompleto:
        venta.productionId === null
          ? false
          : (producciones.get(venta.productionId)?.costoIncompleto ?? false),
    })),
    sobrantes: destinos.map((destino) => ({
      fecha: destino.fecha,
      tipo: destino.tipo,
      porciones: destino.porciones,
      costoCentavos: destino.costoCentavos,
    })),
  };
}

function conEtiquetas(resumen: ResumenPeriodo): ResumenConDestinos {
  return {
    ...resumen,
    sobrantesLegibles: resumen.sobrantesPorDestino.map((s) => ({
      tipo: s.tipo as DestinoSobrante,
      etiqueta: DESTINOS_SOBRANTE[s.tipo as DestinoSobrante] ?? s.tipo,
      porciones: s.porciones,
      costoCentavos: s.costoCentavos,
    })),
  };
}

/** Resumen de un dia, una semana o un mes. */
export async function verResumen(
  granularidad: Granularidad,
  fecha: FechaLocal = hoy(),
): Promise<ResumenConDestinos> {
  const { desde, hasta } = rangoDe(granularidad, fecha);
  const { ventas, sobrantes } = await datosDelRango(desde, hasta);
  return conEtiquetas(resumirPeriodo(desde, hasta, ventas, sobrantes));
}

export async function verResumenDeRango(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<ResumenConDestinos> {
  const { ventas, sobrantes } = await datosDelRango(desde, hasta);
  return conEtiquetas(resumirPeriodo(desde, hasta, ventas, sobrantes));
}

// ---------------------------------------------------------------------------
// Desglose mensual por semanas
// ---------------------------------------------------------------------------

export interface TrozoDelMes {
  numero: number;
  desde: FechaLocal;
  hasta: FechaLocal;
  etiqueta: string;
  resumen: ResumenPeriodo;
}

export interface DesgloseMensual {
  etiqueta: string;
  mes: ResumenConDestinos;
  semanas: TrozoDelMes[];
}

/**
 * Mes completo, desglosado por semanas (Req. 16.2).
 *
 * Las semanas van recortadas al mes, asi que sus totales suman el total del mes.
 */
export async function verDesgloseMensual(
  fecha: FechaLocal = hoy(),
): Promise<DesgloseMensual> {
  const mes = await verResumen('mes', fecha);
  const { ventas, sobrantes } = await datosDelRango(mes.desde, mes.hasta);

  return {
    etiqueta: etiquetaMes(fecha),
    mes,
    semanas: semanasDelMes(fecha).map((trozo) => ({
      ...trozo,
      etiqueta: `Semana ${trozo.numero}`,
      resumen: resumirPeriodo(trozo.desde, trozo.hasta, ventas, sobrantes),
    })),
  };
}

// ---------------------------------------------------------------------------
// Detalle de las ventas
// ---------------------------------------------------------------------------

export interface RenglonVenta {
  venta: Sale;
  nombreReceta: string;
  fechaLegible: string;
  porcionesPlaneadas: number;
  porcionesNoVendidas: number;
  /** Ganancia de lo vendido: ingreso menos el costo prorrateado. */
  gananciaCentavos: Centavos | null;
  margenPorcentaje: number | null;
  /** true si su produccion llevaba estimacion. */
  estimado: boolean;
  /** true si no hay costo del cual partir. */
  costoNoDisponible: boolean;
  /** Destinos de las porciones no vendidas de ese platillo. */
  destinos: Array<{ etiqueta: string; porciones: number; costoCentavos: Centavos | null }>;
  /** true si esta venta corrige a otra. */
  esCorreccion: boolean;
}

/** Ventas de un rango, con lo necesario para mostrarlas y revisarlas. */
export async function verVentas(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<RenglonVenta[]> {
  const ventas = await ventasEntre(desde, hasta);
  if (ventas.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(ventas.map((v) => v.recipeId))]))
      .filter((r): r is Recipe => r !== undefined)
      .map((r) => [r.id, r]),
  );

  const idsPlatillo = [
    ...new Set(ventas.map((v) => v.plannedItemId).filter((x): x is string => x !== null)),
  ];
  const platillos = new Map(
    (await db.plannedItems.bulkGet(idsPlatillo))
      .filter((p): p is PlannedItem => p !== undefined)
      .map((p) => [p.id, p]),
  );

  const producciones = new Map((await db.productions.toArray()).map((p) => [p.id, p]));
  const destinos = await db.saleDispositions.toArray();

  return ventas
    .map((venta): RenglonVenta => {
      const platillo = venta.plannedItemId === null ? undefined : platillos.get(venta.plannedItemId);
      const produccion = venta.productionId === null ? undefined : producciones.get(venta.productionId);

      /**
       * Destinos de ESTA venta, no del lote.
       *
       * Los registros con `saleId` se atribuyen a su venta. Los anteriores a ese
       * campo solo se pueden ubicar por lote, y se muestran unicamente cuando la
       * venta es la unica del platillo: de otro modo cada captura mostraria los
       * sobrantes de todas y las porciones no vendidas saldrian repetidas.
       */
      const delPlatillo = destinos.filter((d) => d.plannedItemId === venta.plannedItemId);
      const conDueno = delPlatillo.filter((d) => d.saleId === venta.id);
      const sinDueno = delPlatillo.filter((d) => d.saleId === undefined);
      const capturasDelPlatillo = ventas.filter(
        (v) => v.plannedItemId === venta.plannedItemId,
      ).length;

      const susDestinos =
        sinDueno.length > 0 && capturasDelPlatillo === 1 ? [...conDueno, ...sinDueno] : conDueno;

      const porcionesNoVendidas = susDestinos.reduce((s, d) => s + d.porciones, 0);

      const ganancia =
        venta.costoCentavos === null ? null : venta.ingresoCentavos - venta.costoCentavos;

      return {
        venta,
        nombreReceta: recetas.get(venta.recipeId)?.nombre ?? '(receta eliminada)',
        fechaLegible: formatearMedio(venta.fecha),
        porcionesPlaneadas: platillo?.porcionesPlaneadas ?? venta.porcionesVendidas,
        porcionesNoVendidas,
        gananciaCentavos: ganancia,
        margenPorcentaje:
          ganancia === null || venta.ingresoCentavos <= 0
            ? null
            : (ganancia / venta.ingresoCentavos) * 100,
        estimado: produccion?.costoEstimado ?? false,
        costoNoDisponible: venta.costoCentavos === null,
        destinos: susDestinos.map((d) => ({
          etiqueta: DESTINOS_SOBRANTE[d.tipo] ?? d.tipo,
          porciones: d.porciones,
          costoCentavos: d.costoCentavos,
        })),
        esCorreccion: venta.corrigeA !== null,
      };
    })
    .sort((a, b) => b.venta.fecha.localeCompare(a.venta.fecha));
}

// ---------------------------------------------------------------------------
// Por platillo
// ---------------------------------------------------------------------------

export interface ResultadoPorReceta {
  recipeId: string;
  nombreReceta: string
  porcionesVendidas: number;
  ingresoCentavos: Centavos;
  costoVendidoCentavos: Centavos;
  gananciaCentavos: Centavos;
  margenPorcentaje: number | null;
  costoIncompleto: boolean;
}

/**
 * Que platillo deja mas dinero en un rango.
 *
 * Es la pregunta que sigue naturalmente a tener el historial: si la lasaña deja
 * 11 % y la milanesa 51 %, conviene saberlo antes de armar el menu.
 */
export async function resultadoPorReceta(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<ResultadoPorReceta[]> {
  const ventas = await ventasEntre(desde, hasta);
  if (ventas.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(ventas.map((v) => v.recipeId))]))
      .filter((r): r is Recipe => r !== undefined)
      .map((r) => [r.id, r]),
  );

  const porReceta = new Map<string, ResultadoPorReceta>();

  for (const venta of ventas) {
    const previo = porReceta.get(venta.recipeId) ?? {
      recipeId: venta.recipeId,
      nombreReceta: recetas.get(venta.recipeId)?.nombre ?? '(receta eliminada)',
      porcionesVendidas: 0,
      ingresoCentavos: 0,
      costoVendidoCentavos: 0,
      gananciaCentavos: 0,
      margenPorcentaje: null,
      costoIncompleto: false,
    };

    previo.porcionesVendidas += venta.porcionesVendidas;
    previo.ingresoCentavos += venta.ingresoCentavos;
    previo.costoVendidoCentavos += venta.costoCentavos ?? 0;
    if (venta.costoCentavos === null) previo.costoIncompleto = true;

    porReceta.set(venta.recipeId, previo);
  }

  return [...porReceta.values()]
    .map((r) => {
      const ganancia = r.ingresoCentavos - r.costoVendidoCentavos;
      return {
        ...r,
        gananciaCentavos: ganancia,
        margenPorcentaje: r.ingresoCentavos > 0 ? (ganancia / r.ingresoCentavos) * 100 : null,
      };
    })
    .sort((a, b) => b.gananciaCentavos - a.gananciaCentavos);
}

// ---------------------------------------------------------------------------
// Meses con datos, para poder navegar
// ---------------------------------------------------------------------------

/** Meses que tienen al menos una venta, del mas reciente al mas antiguo. */
export async function mesesConVentas(): Promise<Array<{ clave: string; etiqueta: string }>> {
  const ventas = await db.sales.toArray();
  const claves = new Set(ventas.map((v) => v.fecha.slice(0, 7)));

  return [...claves]
    .sort((a, b) => b.localeCompare(a))
    .map((clave) => ({ clave, etiqueta: etiquetaMes(`${clave}-01`) }));
}
